import { useIsMobile } from 'src/hooks/useIsMobile'
import { MembershipData } from 'src/types/membership'
import { getUserStatus } from 'src/pages/membership/components/MembershipBenefits/utils'
import { useUnityPassTrialState } from 'src/pages/unityPass/hooks/useUnityPassTrialState'

import { ReactComponent as ChevronRight } from 'src/assets/icons/chevron-right.svg'
import { ReactComponent as UnityPassLogo } from 'src/assets/svg/unitypass/unitypass-label.svg'

import './UnityPassCheckoutBanner.scss'

interface UnityPassCheckoutBannerProps {
  membershipData: MembershipData
  handlePurchase: () => void
}

enum CopyVariant {
  Default = 'default',
  Trial = 'trial'
}
type Copy = {
  title: string
  description: string
  ctaLabel: string
}

const copy: Record<CopyVariant, Copy> = {
  [CopyVariant.Default]: {
    title: 'Save $60/Month with Free Delivery',
    description:
      'Join UnityPass to enjoy free delivery, exclusive discounts, and member-only perks.',
    ctaLabel: 'Join now'
  },
  [CopyVariant.Trial]: {
    title: 'This Month is On Us – Only {days} Days Left',
    description:
      'Enjoy free delivery while it lasts — lock in your perks and keep saving with UnityPass.',
    ctaLabel: 'Join now'
  }
}

export const UnityPassCheckoutBanner = ({
  membershipData,
  handlePurchase
}: UnityPassCheckoutBannerProps) => {
  const membershipStatus = getUserStatus(membershipData?.activation)
  const { isTrial, daysUntilTrialEnd } = useUnityPassTrialState(membershipData)
  const isMobile = useIsMobile()

  const shouldShowBanner =
    membershipStatus === 'non-member' ||
    (membershipStatus === 'rejoin' && isTrial)

  if (!shouldShowBanner) {
    return null
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handlePurchase()
    }
  }

  const copyVariant = isTrial ? CopyVariant.Trial : CopyVariant.Default
  const { title, description, ctaLabel } = copy[copyVariant]

  return (
    <div
      className="unity-pass-checkout-banner"
      data-testid="unity-pass-checkout-banner"
      role={isMobile ? 'button' : undefined}
      tabIndex={isMobile ? 0 : undefined}
      onClick={isMobile ? handlePurchase : undefined}
      onKeyDown={isMobile ? handleKeyDown : undefined}>
      <div className="unity-pass-checkout-banner__content">
        <UnityPassLogo className="unity-pass-checkout-banner__logo" />
        <div className="unity-pass-checkout-banner__text">
          <h3>{title.replace('{days}', daysUntilTrialEnd.toString())}</h3>
          <p>{description}</p>
        </div>
        {isMobile ? (
          <ChevronRight className="unity-pass-checkout-banner__arrow" />
        ) : (
          <button
            className="unity-pass-checkout-banner__button"
            onClick={handlePurchase}
            data-testid="unity-pass-checkout-purchase-button">
            {ctaLabel}
          </button>
        )}
      </div>
    </div>
  )
}
